<template>
  <NavBar />

  <!-- Hero / Sobre mí -->
  <section id="sobre-mi" class="home-section home-section--ink py-4 py-md-5">
    <div class="container hero d-flex flex-column flex-lg-row-reverse align-items-center justify-content-center gap-4 gap-lg-5">
      <div
        ref="heroFotoShellRef"
        class="hero-foto-shell"
        :class="{ 'hero-foto-shell--in-view': heroFotoInView }"
      >
        <div class="hero-foto">
          <img
            class="hero-foto__img"
            :src="heroFotoSrc"
            :alt="t('hero.photoAlt')"
            loading="eager"
            decoding="async"
          >
        </div>
      </div>
      <div class="hero-texto text-center text-lg-start">
        <h1 class="hero-titulo mb-3">
          <span class="hero-titulo__lineas" :aria-label="t('hero.disciplines')">
            <template v-for="(item, index) in heroDisciplines" :key="index">
              <span class="hero-titulo__item">{{ item }}</span>
              <span
                v-if="index < heroDisciplines.length - 1"
                class="hero-titulo__sep"
                :class="{
                  'hero-titulo__sep--after-pair': index === 1,
                  'hero-titulo__sep--second-line': index === 2,
                }"
                aria-hidden="true"
              >·</span>
              <span
                v-if="index === 1 && heroDisciplines.length > 2"
                class="hero-titulo__break"
                aria-hidden="true"
              />
              <span
                v-if="index === 2 && heroDisciplines.length > 3"
                class="hero-titulo__break hero-titulo__break--xs"
                aria-hidden="true"
              />
            </template>
          </span>
          <span class="hero-cita">{{ t('hero.quote') }}</span>
        </h1>
        <p class="hero-subtitulo mb-3 mb-md-4">
          <span class="hero-subtitulo__nombre">Eduardo Gajardo Schmidlin</span>
          <span class="hero-subtitulo__rol">{{ t('hero.role') }}</span>
        </p>
        <a
          class="hero-cta"
          :href="heroWhatsappHref"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="hero-cta__label">{{ t('hero.cta') }}</span>
        </a>
      </div>
    </div>
    <SectionScrollUp />
  </section>

  <!-- Reproductor de video (franja provisional) -->
  <section id="video" class="home-section home-section--slate py-2">
    <div class="container">
      <div class="video-player-shell">
        <div class="video-player-placeholder" role="region" :aria-label="t('video.region')">
          <svg class="video-player-icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445"/>
          </svg>
          <p class="video-player-label mb-0">{{ t('video.label') }}</p>
        </div>
      </div>
      <SectionScrollUp />
    </div>
  </section>

  <!-- Servicios -->
  <section id="servicios" class="home-section home-section--slate py-4 py-md-5">
    <div class="container">
      <h2 class="section-title mb-4 mb-md-5">{{ t('nav.servicios') }}</h2>
    </div>
    <ProductCarousel
      :items="serviciosLocalizados"
      :aria-label="t('nav.servicios')"
      compact
      :hint="t('carousel.moreServices')"
    >
      <template #default="{ item: servicio }">
        <article class="servicio-card h-100 d-flex flex-column">
          <div v-if="servicio.image" class="servicio-card__media">
            <img
              :src="servicio.image"
              :alt="t(`services.${servicio.titleKey}`)"
              class="servicio-card__img"
              loading="lazy"
            >
          </div>
          <div class="servicio-card__body d-flex flex-column flex-grow-1">
            <h3 class="servicio-card__titulo">{{ t(`services.${servicio.titleKey}`) }}</h3>
            <p class="servicio-card__descripcion">{{ servicio.descripcion }}</p>
            <div v-if="servicio.detalle" class="servicio-card__actions mt-auto">
              <button
                type="button"
                class="btn btn-link servicio-card__mas"
                @click="openServicioDetalle(servicio)"
              >
                {{ t('ui.seeMore') }}
              </button>
            </div>
          </div>
        </article>
      </template>
    </ProductCarousel>
    <div class="container">
      <SectionScrollUp />
    </div>
  </section>

  <!-- Trayectoria -->
  <section id="trayectoria" class="home-section home-section--ink home-section--trayectoria py-4 py-md-5">
    <div class="container trayectoria">
      <h2 class="section-title mb-3 mb-md-4">{{ t('nav.trayectoria') }}</h2>

      <div
        ref="trayectoriaItemsRef"
        class="trayectoria__carousel"
        :class="{ 'trayectoria__carousel--in-view': trayectoriaInView }"
      >
        <button
          type="button"
          class="trayectoria__arrow trayectoria__arrow--prev"
          :aria-label="t('career.prev')"
          @click="trayectoriaPrev"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
            <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
          </svg>
        </button>

        <div class="trayectoria__viewport">
          <div
            class="trayectoria__track"
            :style="{ '--trayectoria-slide': trayectoriaSlide }"
          >
            <article
              v-for="(item, index) in trayectoriaLocalizada"
              :key="item.mark"
              class="trayectoria__item"
              :class="{ 'trayectoria__item--in-view': trayectoriaInView }"
              :style="{ '--trayectoria-i': index }"
            >
              <span class="trayectoria__mark" aria-hidden="true">{{ item.mark }}</span>
              <div class="trayectoria__body">
                <h3 class="trayectoria__item-title">{{ item.title }}</h3>
                <p class="trayectoria__lead mb-0">{{ item.text }}</p>
              </div>
            </article>
          </div>
        </div>

        <button
          type="button"
          class="trayectoria__arrow trayectoria__arrow--next"
          :aria-label="t('career.next')"
          @click="trayectoriaNext"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
            <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/>
          </svg>
        </button>
      </div>

      <div class="trayectoria__dots" role="tablist" :aria-label="t('career.aria')">
        <button
          v-for="(item, index) in trayectoriaLocalizada"
          :key="`dot-${item.mark}`"
          type="button"
          class="trayectoria__dot"
          :class="{ 'trayectoria__dot--active': trayectoriaSlide === index }"
          role="tab"
          :aria-selected="trayectoriaSlide === index"
          :aria-label="t('career.goTo', { title: item.title })"
          @click="goToTrayectoria(index)"
        >
          {{ item.mark }}
        </button>
      </div>

      <SectionScrollUp />
    </div>
  </section>

  <ServiceDetailModal
    :open="servicioModalOpen"
    :servicio="servicioModalActivo"
    :titulo="servicioModalActivo ? t(`services.${servicioModalActivo.titleKey}`) : ''"
    :cta-label="servicioModalActivo?.ctaLabel || 'Cotiza aquí'"
    :cotiza-href="servicioModalActivo ? cotizaMailto(t(`services.${servicioModalActivo.titleKey}`)) : '#'"
    @close="closeServicioDetalle"
  />

  <!-- Productos Sinfónicos -->
  <section id="productos-sinfonicos" class="home-section home-section--ink pt-4 pt-md-5 pb-2 pb-md-3">
    <div class="container text-center">
      <h2 class="section-title section-title--stacked mb-4">
        <span class="section-title__main">{{ t('nav.productosSinfonicos') }}</span>
        <span class="section-title__sub">{{ t('nav.disponibilidadInmediata') }}</span>
      </h2>
      <div v-if="!productosComoCarrusel" class="productos-grid">
        <CardComponent
          v-for="proyecto in productosLocalizados"
          :key="proyecto.id"
          :product-id="proyecto.id"
          :title="proyecto.title"
          :subtitle="proyecto.subtitle || ''"
          :image="proyecto.image"
          :opciones="proyecto.opciones"
          :temas="proyecto.temas || []"
          :agotado="Boolean(proyecto.agotado)"
          :image-position="proyecto.imagePosition || ''"
        />
      </div>
    </div>
    <ProductCarousel
      v-if="productosComoCarrusel"
      :items="productosLocalizados"
      :aria-label="t('nav.productosSinfonicos')"
      compact
    />
    <SectionScrollUp />
  </section>

  <!-- Testimonios (placeholder) -->
  <section id="testimonios" class="home-section home-section--ink py-4 py-md-5">
    <div class="container">
      <h2 class="section-title mb-3">{{ t('nav.testimonios') }}</h2>
      <p class="section-placeholder mb-0">{{ t('testimonials.soon') }}</p>
      <SectionScrollUp />
    </div>
  </section>

  <div id="contacto">
    <FooterComponent />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import NavBar from '../components/NavBar'
import FooterComponent from '../components/FooterComponent.vue'
import ProductCarousel from '../components/ProductCarousel.vue'
import CardComponent from '../components/CardComponent.vue'
import SectionScrollUp from '../components/SectionScrollUp.vue'
import ServiceDetailModal from '../components/ServiceDetailModal.vue'
import productosSinfonicos from '../data/productosSinfonicos.json'
import servicios from '../data/servicios.json'
import { useI18n } from '@/i18n/useI18n'
import { localizeProducts, localizeServicios } from '@/i18n/localizeContent'
import { getWhatsAppHeroUrl } from '@/config/whatsapp'

const { locale, t } = useI18n()
const heroWhatsappHref = computed(() => getWhatsAppHeroUrl(t.value('whatsapp.hero')))
const serviciosLocalizados = computed(() => localizeServicios(servicios, locale.value))
const productosLocalizados = computed(() => localizeProducts(productosSinfonicos, locale.value))

/** Disciplinas del H1: en móvil van en 2 líneas (2 + 2). */
const heroDisciplines = computed(() => {
  const raw = t.value('hero.disciplines')
  return String(raw)
    .split(/\s*[—–-]\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
})

/** Correo provisional para cotizaciones de servicios */
const SERVICIOS_EMAIL = 'paulagajardosch@gmail.com'

function cotizaMailto(titulo) {
  const subject = encodeURIComponent(t.value('ui.quoteSubject', { title: titulo }))
  const body = encodeURIComponent(t.value('ui.quoteBody', { title: titulo }))
  return `mailto:${SERVICIOS_EMAIL}?subject=${subject}&body=${body}`
}

const servicioModalOpen = ref(false)
const servicioModalActivo = ref(null)

function openServicioDetalle(servicio) {
  servicioModalActivo.value = servicio
  servicioModalOpen.value = true
}

function closeServicioDetalle() {
  servicioModalOpen.value = false
  servicioModalActivo.value = null
}

/** Foto del director en `public/img/image_maestro.jpg` */
const heroFotoSrc = '/img/image_maestro.jpg'

const trayectoriaLocalizada = computed(() =>
  ['i', 'ii', 'iii'].map((key, index) => ({
    mark: ['I', 'II', 'III'][index],
    title: t.value(`career.${key}.title`),
    text: t.value(`career.${key}.text`),
  })),
)

const heroFotoShellRef = ref(null)
const heroFotoInView = ref(false)
const trayectoriaItemsRef = ref(null)
const trayectoriaInView = ref(false)
const trayectoriaSlide = ref(0)
const reduceMotion = ref(false)
/** Carrusel cuando no caben las 3 tarjetas en fila (< 992px). */
const productosComoCarrusel = ref(true)

let heroFotoObserver = null
let trayectoriaObserver = null
let productosLayoutMql = null

function syncProductosLayout() {
  if (typeof window === 'undefined') return
  productosComoCarrusel.value = !window.matchMedia('(min-width: 992px)').matches
}

function goToTrayectoria(index) {
  const total = trayectoriaLocalizada.value.length
  trayectoriaSlide.value = ((index % total) + total) % total
}

function trayectoriaPrev() {
  goToTrayectoria(trayectoriaSlide.value - 1)
}

function trayectoriaNext() {
  goToTrayectoria(trayectoriaSlide.value + 1)
}

function setupHeroFotoReveal() {
  if (reduceMotion.value) {
    heroFotoInView.value = true
    return
  }
  const shell = heroFotoShellRef.value
  if (!shell) {
    heroFotoInView.value = true
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    heroFotoInView.value = true
    return
  }
  heroFotoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          heroFotoInView.value = true
          heroFotoObserver?.unobserve(entry.target)
        }
      }
    },
    { threshold: 0.22, rootMargin: '0px 0px -8% 0px' },
  )
  heroFotoObserver.observe(shell)
}

function setupTrayectoriaReveal() {
  if (reduceMotion.value) {
    trayectoriaInView.value = true
    return
  }
  const root = trayectoriaItemsRef.value
  if (!root) {
    trayectoriaInView.value = true
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    trayectoriaInView.value = true
    return
  }
  trayectoriaObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          trayectoriaInView.value = true
          trayectoriaObserver?.unobserve(entry.target)
        }
      }
    },
    { threshold: 0.18, rootMargin: '0px 0px -6% 0px' },
  )
  trayectoriaObserver.observe(root)
}

onMounted(() => {
  reduceMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  setupHeroFotoReveal()
  setupTrayectoriaReveal()
  syncProductosLayout()
  productosLayoutMql = window.matchMedia('(min-width: 992px)')
  productosLayoutMql.addEventListener('change', syncProductosLayout)
})

onUnmounted(() => {
  heroFotoObserver?.disconnect()
  heroFotoObserver = null
  trayectoriaObserver?.disconnect()
  trayectoriaObserver = null
  productosLayoutMql?.removeEventListener('change', syncProductosLayout)
  productosLayoutMql = null
})

</script>

<style scoped>
.home-section {
  background-color: var(--ms-ink);
}

.home-section--ink {
  background-color: var(--ms-ink);
}

.home-section--slate {
  background-color: var(--ms-slate);
}

.section-title {
  font-family: var(--font-display);
  font-size: clamp(1.3rem, 4vw, 1.9rem);
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--ms-text);
  position: relative;
  display: inline-block;
  padding-bottom: 0.75rem;
}

.section-title--stacked {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}

.section-title__sub {
  font-family: var(--font-body);
  font-size: clamp(0.95rem, 2.4vw, 1.12rem);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: var(--ms-text-muted);
}

.section-title::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  width: clamp(2.5rem, 18vw, 3.75rem);
  height: 3px;
  transform: translateX(-50%);
  border-radius: 2px;
  background: linear-gradient(
    90deg,
    var(--ms-accent) 0%,
    rgba(var(--ms-accent-rgb), 0.5) 100%
  );
  opacity: 0.88;
}

.section-placeholder {
  font-family: var(--font-body);
  color: var(--ms-text-muted);
  font-size: clamp(0.9rem, 2.2vw, 1rem);
}

.home-section--trayectoria {
  position: relative;
  isolation: isolate;
  overflow: hidden;
}

.home-section--trayectoria::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(
      ellipse 55% 50% at 12% 35%,
      rgba(var(--ms-accent-rgb), 0.14) 0%,
      transparent 65%
    ),
    radial-gradient(
      ellipse 45% 40% at 92% 70%,
      rgba(var(--ms-accent-rgb), 0.08) 0%,
      transparent 70%
    );
}

.trayectoria {
  max-width: 46rem;
  margin-inline: auto;
  text-align: center;
}

.trayectoria__carousel {
  position: relative;
  text-align: left;
}

/* Controles solo en md+ */
.trayectoria__arrow,
.trayectoria__dots {
  display: none;
}

.trayectoria__viewport {
  position: relative;
}

.trayectoria__track {
  display: grid;
  gap: 1.35rem;
  position: relative;
}

/* Móvil: eje vertical */
.trayectoria__track::before {
  content: '';
  position: absolute;
  left: 1.15rem;
  top: 0.55rem;
  bottom: 0.55rem;
  width: 1px;
  background: linear-gradient(
    180deg,
    rgba(var(--ms-accent-rgb), 0.75) 0%,
    rgba(var(--ms-accent-rgb), 0.2) 100%
  );
}

.trayectoria__item {
  display: grid;
  grid-template-columns: 2.3rem minmax(0, 1fr);
  gap: 0.75rem 0.95rem;
  align-items: start;
  opacity: 0;
  transform: translateX(-1.1rem);
  transition:
    opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: calc(var(--trayectoria-i, 0) * 0.12s);
}

.trayectoria__item--in-view {
  opacity: 1;
  transform: translateX(0);
}

.trayectoria__mark {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.3rem;
  height: 2.3rem;
  font-family: var(--font-display);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: #e8f0fb;
  background:
    radial-gradient(
      circle at 35% 30%,
      rgba(168, 196, 232, 0.4) 0%,
      rgba(var(--ms-accent-rgb), 0.5) 42%,
      rgba(var(--ms-deep-rgb), 0.98) 78%
    );
  border: 1px solid rgba(var(--ms-accent-rgb), 0.65);
  border-radius: 50%;
  box-shadow:
    0 0 0 4px var(--ms-ink),
    0 0 16px rgba(var(--ms-accent-rgb), 0.3);
  line-height: 1;
  justify-self: center;
}

.trayectoria__body {
  min-width: 0;
}

.trayectoria__item-title {
  font-family: var(--font-display);
  font-size: clamp(0.92rem, 2vw, 1.05rem);
  font-weight: 600;
  letter-spacing: 0.045em;
  line-height: 1.3;
  color: var(--ms-text);
  margin: 0 0 0.55rem;
  position: relative;
  display: inline-block;
  padding-bottom: 0.35rem;
}

.trayectoria__item-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 2rem;
  height: 2px;
  border-radius: 1px;
  background: linear-gradient(
    90deg,
    var(--ms-accent-on-dark) 0%,
    rgba(var(--ms-accent-rgb), 0.12) 100%
  );
}

.trayectoria__lead {
  font-family: var(--font-body);
  font-size: clamp(0.74rem, 1.55vw, 0.82rem);
  line-height: 1.6;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.68);
  text-align: justify;
  text-wrap: pretty;
  hyphens: auto;
  padding-right: 0.85rem;
}

/* md+: un hito a la vez, carrusel lateral */
@media (min-width: 768px) {
  .trayectoria {
    max-width: none;
    width: 100%;
  }

  .trayectoria__carousel {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem 1.15rem;
  }

  .trayectoria__arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.55rem;
    height: 2.55rem;
    padding: 0;
    border: 1px solid rgba(var(--ms-accent-rgb), 0.55);
    border-radius: 50%;
    background: rgba(var(--ms-accent-rgb), 0.16);
    color: var(--ms-accent-on-dark);
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      transform 0.15s ease;
  }

  .trayectoria__arrow:hover,
  .trayectoria__arrow:focus-visible {
    background: rgba(var(--ms-accent-rgb), 0.3);
    border-color: rgba(var(--ms-accent-rgb), 0.9);
  }

  .trayectoria__arrow:active {
    transform: scale(0.96);
  }

  .trayectoria__arrow:focus-visible {
    outline: 2px solid rgba(var(--ms-accent-rgb), 0.45);
    outline-offset: 2px;
  }

  .trayectoria__viewport {
    overflow: hidden;
    min-width: 0;
  }

  .trayectoria__track {
    display: flex;
    gap: 0;
    transition: transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
    transform: translateX(calc(var(--trayectoria-slide, 0) * -100%));
  }

  .trayectoria__track::before {
    display: none;
  }

  .trayectoria__item {
    flex: 0 0 100%;
    width: 100%;
    grid-template-columns: 2.7rem minmax(0, 1fr);
    gap: 1rem 1.25rem;
    padding: 0.35rem 0.25rem 0.5rem;
    opacity: 1;
    transform: none;
    transition: none;
  }

  .trayectoria__carousel--in-view .trayectoria__item {
    opacity: 1;
    transform: none;
  }

  .trayectoria__mark {
    width: 2.7rem;
    height: 2.7rem;
    font-size: 0.88rem;
  }

  .trayectoria__item-title {
    font-size: clamp(1rem, 2vw, 1.15rem);
  }

  .trayectoria__item-title::after {
    width: 2.4rem;
  }

  .trayectoria__lead {
    font-size: clamp(0.84rem, 1.45vw, 0.95rem);
    line-height: 1.7;
    max-width: none;
    padding-right: 0;
  }

  .trayectoria__dots {
    display: flex;
    justify-content: center;
    gap: 0.55rem;
    margin-top: 1.15rem;
  }

  .trayectoria__dot {
    min-width: 2.1rem;
    height: 2.1rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    border: 1px solid rgba(var(--ms-accent-rgb), 0.4);
    background: transparent;
    color: rgba(255, 255, 255, 0.65);
    font-family: var(--font-display);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      color 0.2s ease;
  }

  .trayectoria__dot:hover,
  .trayectoria__dot:focus-visible {
    border-color: rgba(var(--ms-accent-rgb), 0.8);
    color: var(--ms-text);
  }

  .trayectoria__dot--active {
    background: rgba(var(--ms-accent-rgb), 0.28);
    border-color: rgba(var(--ms-accent-rgb), 0.85);
    color: var(--ms-accent-on-dark);
  }
}

@media (prefers-reduced-motion: reduce) {
  .trayectoria__item {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .trayectoria__track {
    transition: none;
  }
}

/* Hero */
.hero-foto-shell {
  --foto-ancho: min(100%, 22rem);
  flex: 0 0 auto;
  width: var(--foto-ancho);
  margin-inline: auto;
  position: relative;
  opacity: 0;
  transform: scale(0.96) translateY(14px);
  transition:
    opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-foto-shell--in-view {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.hero-foto {
  width: 100%;
  position: relative;
  overflow: hidden;
  background: transparent;
  /* Sin borde duro: la viñeta funde la foto con el fondo de la sección */
  -webkit-mask-image: radial-gradient(
    ellipse 92% 88% at 50% 42%,
    #000 42%,
    rgba(0, 0, 0, 0.85) 62%,
    rgba(0, 0, 0, 0.35) 78%,
    transparent 100%
  );
  mask-image: radial-gradient(
    ellipse 92% 88% at 50% 42%,
    #000 42%,
    rgba(0, 0, 0, 0.85) 62%,
    rgba(0, 0, 0, 0.35) 78%,
    transparent 100%
  );
}

.hero-foto::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse 78% 72% at 50% 40%,
    transparent 48%,
    rgba(10, 10, 10, 0.45) 72%,
    var(--ms-ink) 100%
  );
}

@media (min-width: 992px) {
  .hero-texto {
    flex: 1 1 0;
    max-width: min(100%, 42rem);
  }

  .hero-foto-shell {
    /* Tablet landscape / desktop estrecho: foto más contenida */
    --foto-ancho: min(36%, 16rem);
    margin-inline: 0;
    flex: 0 1 auto;
  }
}

@media (min-width: 1200px) {
  .hero-foto-shell {
    --foto-ancho: min(38%, 22rem);
  }
}

.hero-foto__img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
}

@media (prefers-reduced-motion: reduce) {
  .hero-foto-shell {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

.hero-texto {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  position: relative;
  z-index: 1;
}

@media (min-width: 992px) {
  .hero-texto {
    flex: 1 1 0;
    max-width: min(100%, 38rem);
  }
}

.hero-titulo {
  font-family: var(--font-display);
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.03em;
  color: var(--ms-text);
}

.hero-titulo__lineas {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  column-gap: 0;
  row-gap: 0.28rem;
  max-width: min(100%, 24rem);
  margin-inline: auto;
  font-size: clamp(0.88rem, 4.2vw, 1.12rem);
  line-height: 1.4;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 600;
}

.hero-titulo__item {
  white-space: nowrap;
}

.hero-titulo__sep {
  margin-inline: 0.32rem 0.4rem;
  opacity: 0.5;
  font-weight: 400;
  letter-spacing: 0;
}

.hero-titulo__break {
  flex-basis: 100%;
  width: 100%;
  height: 0;
}

/* Solo en pantallas muy estrechas: 3.ª y 4.ª disciplina en filas aparte */
.hero-titulo__break--xs {
  display: none;
}

/* Moto G4 / tablets (Surface Pro en portrait): 2+2 con punto en ambas filas */
@media (max-width: 991.98px) {
  .hero-titulo__sep--after-pair {
    display: none;
  }
}

@media (max-width: 359.98px) {
  .hero-titulo__break--xs {
    display: block;
  }

  .hero-titulo__sep--second-line {
    display: none;
  }

  .hero-titulo__lineas {
    font-size: clamp(0.9rem, 5vw, 1.05rem);
    letter-spacing: 0.04em;
  }
}

.hero-cita {
  display: block;
  margin-top: 0.55rem;
  font-style: italic;
  color: var(--ms-accent-on-dark);
  font-weight: 600;
  white-space: pre-line;
  overflow-wrap: break-word;
  line-height: 1.4;
  letter-spacing: 0.01em;
  text-transform: none;
  font-size: clamp(0.9rem, 3.9vw, 1.1rem);
}

@media (min-width: 360px) and (max-width: 991.98px) {
  .hero-titulo__lineas {
    max-width: min(100%, 28rem);
  }
}

@media (min-width: 576px) {
  .hero-titulo__lineas {
    font-size: clamp(1rem, 2.6vw, 1.18rem);
  }
}

@media (min-width: 992px) {
  .hero-titulo__lineas {
    justify-content: flex-start;
    max-width: none;
    margin-inline: 0;
    row-gap: 0.2rem;
    font-size: clamp(0.95rem, 1.15vw, 1.18rem);
  }

  .hero-titulo__break {
    display: none;
  }

  .hero-cita {
    margin-top: 0.45rem;
  }
}

.hero-subtitulo {
  font-family: var(--font-body);
  line-height: 1.55;
  color: var(--ms-text-muted);
}

.hero-subtitulo__nombre,
.hero-subtitulo__rol {
  display: block;
  white-space: normal;
}

.hero-subtitulo__nombre {
  font-size: clamp(0.58rem, 2.7vw, 0.92rem);
}

.hero-subtitulo__rol {
  font-size: clamp(0.54rem, 2.4vw, 0.82rem);
}

.hero-cta {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.15rem;
  padding: 0.72rem 1.85rem 0.78rem;
  border: 1px solid rgba(var(--ms-accent-rgb), 0.55);
  border-radius: 999px;
  background: linear-gradient(
    165deg,
    rgba(var(--ms-accent-rgb), 0.18) 0%,
    rgba(var(--ms-accent-rgb), 0.05) 100%
  );
  color: var(--ms-accent-on-dark);
  text-decoration: none;
  transition:
    color 0.28s ease,
    background-color 0.28s ease,
    border-color 0.28s ease,
    box-shadow 0.28s ease,
    transform 0.22s ease;
}

.hero-cta__label {
  font-family: var(--font-display);
  font-size: clamp(0.72rem, 1.8vw, 0.88rem);
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  line-height: 1;
}

.hero-cta:hover,
.hero-cta:focus-visible {
  color: #fff;
  background: rgba(var(--ms-accent-rgb), 0.32);
  border-color: rgba(var(--ms-accent-rgb), 0.95);
  box-shadow:
    0 0 0 1px rgba(var(--ms-accent-rgb), 0.28),
    0 10px 28px rgba(0, 0, 0, 0.28);
  transform: translateY(-2px);
}

.hero-cta:focus-visible {
  outline: 2px solid rgba(var(--ms-accent-rgb), 0.55);
  outline-offset: 3px;
}

.hero-cta:active {
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .hero-cta {
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
  }

  .hero-cta:hover,
  .hero-cta:focus-visible,
  .hero-cta:active {
    transform: none;
  }
}

@media (min-width: 1200px) {
  .hero-subtitulo__nombre,
  .hero-subtitulo__rol {
    white-space: nowrap;
  }
}

/* Video */
.video-player-shell {
  max-width: 100%;
  margin-inline: auto;
}

.video-player-placeholder {
  height: clamp(3.25rem, 8vw, 4.25rem);
  border-radius: 0.45rem;
  background: linear-gradient(160deg, #141c2a 0%, #0f0f0f 100%);
  border: 1px solid var(--ms-border);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  color: var(--ms-text-muted);
}

.video-player-icon {
  opacity: 0.45;
  flex-shrink: 0;
}

.video-player-label {
  font-size: 0.85rem;
  letter-spacing: 0.02em;
}

/* Servicios */
.servicio-card {
  text-align: center;
  padding: 0;
  overflow: hidden;
  border-radius: 0.85rem;
  background: var(--ms-surface);
  border: 1px solid var(--ms-border);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.servicio-card:hover {
  transform: translateY(-3px);
  border-color: rgba(var(--ms-accent-rgb), 0.35);
}

.servicio-card__body {
  padding: 1.05rem 1.1rem 1.2rem;
}

.servicio-card__titulo {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.3;
  /* Reserva 2 líneas de título para alinear descripciones */
  min-height: calc(1.05rem * 1.3 * 2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ms-text);
  margin-bottom: 0.65rem;
}

.servicio-card__descripcion {
  font-family: var(--font-body);
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--ms-text-muted);
  text-align: center;
  margin-bottom: 1.15rem;
  flex-grow: 1;
}

.servicio-card__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
}

.servicio-card__mas {
  font-family: var(--font-body);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ms-accent-on-dark);
  text-decoration: none;
  padding: 0.15rem 0.35rem;
}

.servicio-card__mas:hover,
.servicio-card__mas:focus-visible {
  color: var(--ms-text);
  text-decoration: underline;
}

.servicio-card__media {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.servicio-card__media::after {
  content: '';
  position: absolute;
  inset: auto 0 0;
  height: 38%;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, var(--ms-surface));
}

.servicio-card__img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.productos-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  max-width: 64rem;
  margin: 0 auto;
  text-align: left;
}

@media (prefers-reduced-motion: reduce) {
  .servicio-card {
    transition: none;
  }

  .servicio-card:hover {
    transform: none;
  }
}
</style>